import React, { useState, useEffect, useContext } from "react"
import styled from "styled-components"
import AuthContext from "../../context/AuthContext"
import BranchContext from "../../context/BranchContext"
import { Formik, Form, Field, ErrorMessage, getIn } from "formik"
import Personal from "./Personal"
import Delivery from "./Delivery"
import BlurLoader from "../../zzz/organisms/blurLoader"
import { getUser } from "../../services/auth"
import {
  getCustomer,
  getCountries,
  updateCustomer,
  getLatLngFromPostcode,
} from "../../lib/woocommApi"
import { BtnSquare, ButtonRow } from "../../zzz/atoms/buttons"
import { injectScript } from "../../utils/injectScript"
import { rawCheckWindowSize, windowSizes } from "../../utils/responsive"

// "Global" variables for the Google API
var googleAutocomplete = false
var googleMap = false
var googleGeocoder = false
var googleLocationMarker = false

// ======================
// 	🧱🧱 COMPONENT 🧱🧱
// ======================

const Info = ({
  givenAccountData = {},
  onPage = "",
  continueCallback = () => {},
  setReadyToSubmitAccount = () => {},
  setTryToSubmitAccount = () => {},
  tryToSubmitAccount = false,
  condense,
}) => {
  const [loading, setLoading] = useState(true)
  const [changesSaved, setChangesSaved] = useState(false)
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [apiError, setApiError] = useState("")
  const [submitError, setSubmitError] = useState("")
  const [provinceOptions, setProvinceOptions] = useState(false)
  const [countryOptions, setCountryOptions] = useState(false)
  const [accountData, setAccountData] = useState(givenAccountData)
  const [googleData, setGoogleData] = useState({})
  const [pinLatLng, setPinLatLng] = useState(false)
  const [googleSearchText, setGoogleSearchText] = useState("")
  const [showManualAddressSection, setShowManualAddressSection] =
    useState(false)
  const [showManualAddressButton, setShowManualAddressButton] = useState(false)

  const { loggedInState, performLogout } = useContext(AuthContext)
  const { selectedBranch, setSelectedBranch } = useContext(BranchContext)

  useEffect(() => {
    if (googleSearchText && !showManualAddressButton) {
      setShowManualAddressButton(true)
    }
  }, [googleSearchText])

  // Fetch address from WC
  useEffect(() => {
    // Load script once
    const scriptParams = {
      id: "google_maps_api_script",
      parentElement: "head",
      src: `https://maps.googleapis.com/maps/api/js?key=${process.env.GATSBY_GOOGLE_PLACES_API_KEY}&libraries=places`,
      onLoad: googleApiInitialize,
    }
    injectScript(scriptParams)

    if (givenAccountData.id) {
      setLoading(false)
      return
    }
    const { token } = getUser()
    refreshCountryAndProvince(token)
    fetchAddress(token)
  }, [])

  // Run once the Google Places API has finished loading
  const googleApiInitialize = () => {
    // Delete old map marker
    if (googleLocationMarker) {
      googleLocationMarker.setMap(null)
      googleLocationMarker = null
    }
    if (typeof window != "undefined" && window.google) {
      // Default map center at The Mattress Warehouse Randburg
      let mapCenter = { lat: -25.783213635253, lng: 28.29113045415 }

      // Hopefully address has been fetched at this point, set map position
      if (accountData.address_latitude && accountData.address_longitude) {
        const newLatLng = safeSetPinLatLng({
          lat: accountData.address_latitude,
          lng: accountData.address_longitude,
        })
        mapCenter.lat = newLatLng.lat
        mapCenter.lng = newLatLng.lng
      }

      // Init Google map
      googleMap = new window.google.maps.Map(document.getElementById("map"), {
        center: mapCenter,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
      })
      if (rawCheckWindowSize({ maxWidth: windowSizes.mobile })) {
        // Click on map to set marker
        googleMap.addListener("click", (mapsMouseEvent) => {
          saveLatLngToAddress(
            {
              lat: mapsMouseEvent.latLng.lat(),
              lng: mapsMouseEvent.latLng.lng(),
            },
            {
              passive: true,
            }
          )
        })
      }
      // Init Google geocoder (lat/lng to address convert)
      googleGeocoder = new window.google.maps.Geocoder()

      // The input box to attach the Google Places Search API
      var input = document.getElementById("google_address_search")
      // This is the "bias" Google uses, it is more likely to show addresses
      // within this bounding box. Coordinates chosen here is just a random box
      // around South Africa
      var defaultBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(-28.533004, 26.226024),
        new window.google.maps.LatLng(-24.978814, 30.705224)
      )
      var options = {
        bounds: defaultBounds,
        types: ["geocode", "establishment"],
        componentRestrictions: { country: "ZA" },
      }

      // Prepare the "autocomplete" feature and indicate that we only need addresses
      googleAutocomplete = new window.google.maps.places.Autocomplete(
        input,
        options
      )
      googleAutocomplete.setFields([
        "address_component",
        "name",
        "types",
        "geometry",
        "place_id",
      ])
      // The callback function when a user clicks an address
      googleAutocomplete.addListener("place_changed", googlePopulateAddress)

      // If account data has been loaded but user has no address saved, attempt to navigate to browser location
      if (
        accountData.billing &&
        !accountData.address_latitude &&
        !accountData.address_longitude
      ) {
        // Attempt to get user browser location
        useBrowserLocation()
      }
    } else {
      console.error("FAILED to load Google Places API")
    }
  }

  // Save new Lat/Long as floats
  const safeSetPinLatLng = (latLng) => {
    if (latLng && latLng.lat && latLng.lng) {
      const newLat = parseFloat(latLng.lat)
      const newLng = parseFloat(latLng.lng)
      const newLatLng = {
        lat: newLat,
        lng: newLng,
      }
      if (pinLatLng && newLat == pinLatLng.lat && newLng == pinLatLng.lng) {
        return {}
      }
      if (!isNaN(newLat) && !isNaN(newLng)) {
        setPinLatLng(newLatLng)
        return newLatLng
      } else {
        return {}
      }
    }
  }

  // Fetch Address from WooCommerce
  const fetchAddress = (token) => {
    getCustomer(token, loggedInState)
      .then(async (resp) => {
        const body = await resp
        setAccountData(body)
        // If Google Map has been loaded, set the place ID to move the map to this position
        // If map has not yet loaded, it will use the accountData place ID once it has loaded
        if (googleMap && body.address_latitude && body.address_longitude) {
          safeSetPinLatLng({
            lat: body.address_latitude,
            lng: body.address_longitude,
          })
        }
        // If no branch is selected
        // if a selected branch was fetched
        // update the local selected branch based on the fetched branch
        if (
          !selectedBranch &&
          body.selected_branch &&
          typeof body.selected_branch == "string"
        ) {
          setSelectedBranch(body.selected_branch)
        }
        setLoading(false)
      })
      .catch(async (error) => {
        let errorString = "Failed to fetch customer info"
        if (error.message) {
          errorString = error.message
        }
        setApiError(errorString)
        setLoading(false)
      })
  }

  const refreshCountryAndProvince = (token) => {
    setCountriesLoading(true)
    Promise.all([
      getCountries(token, performLogout).then((countryData) => {
        setCountryOptions(countryData)
      }),
      getCountries(token, performLogout, "ZA").then((provinceData) => {
        setProvinceOptions(provinceData)
      }),
    ])
      .then(() => {
        setCountriesLoading(false)
      })
      .catch(() => {
        setCountriesLoading(false)
      })
  }

  // If the selected Place ID changes, fetch the corresponding "place" and update map
  useEffect(() => {
    moveMapToPin()
  }, [pinLatLng])

  const moveMapToPin = () => {
    if (
      pinLatLng &&
      typeof window != "undefined" &&
      window.google &&
      googleMap
    ) {
      if (isNaN(pinLatLng.lat) || isNaN(pinLatLng.lng)) {
        console.log("Cannot move pin, coords are NaN: ", pinLatLng)
        return
      }
      // Move map to new location
      googleMap.panTo(pinLatLng)

      // Mark location on map
      if (googleLocationMarker) {
        googleLocationMarker.setPosition(pinLatLng)
      } else {
        googleLocationMarker = new window.google.maps.Marker({
          position: pinLatLng,
          animation: window.google.maps.Animation.DROP,
          draggable: true,
          opacity: 1,
        })
        // Make marker draggable and add listener to update address when this happens
        googleLocationMarker.addListener(
          "dragend",
          () => {
            saveLatLngToAddress({
              lat: googleLocationMarker.getPosition().lat(),
              lng: googleLocationMarker.getPosition().lng(),
            })
          },
          {
            passive: true,
          }
        )
      }
      googleLocationMarker.setMap(googleMap)
    }
  }

  // Save address from Lat/Lng pin drop coordinates
  const saveLatLngToAddress = (latLng) => {
    if (googleGeocoder && accountData) {
      const newLatLng = safeSetPinLatLng(latLng)
      if (newLatLng && newLatLng.lat && newLatLng.lng) {
        googleGeocoder.geocode({ location: newLatLng }, (results, status) => {
          if (status === "OK") {
            if (results[0]) {
              savePlaceData(results[0])
            } else {
              console.log("No results found")
            }
          }
        })
      }
    }
  }

  // Fetch lat/lng from postcode
  const fetchLatLngFromPostcode = (values) => {
    if (!pinLatLng) {
      const postcode = values
      getLatLngFromPostcode(postcode)
        .then((response) => {
          if (response && response.latitude && response.longitude) {
            safeSetPinLatLng({
              lat: response.latitude,
              lng: response.longitude,
            })
          } else {
            throw response
          }
        })
        .catch((error) =>
          console.error(`Postal code get request failed`, error)
        )
    }
  }

  // Province list refreshed
  useEffect(() => {
    if (provinceOptions && provinceOptions.length > 0) {
      if (accountData.billing) {
        if (!provinceOptions[accountData.billing.state]) {
          // Update values structure
          setAccountData((prev) => {
            let newState = Object.assign({}, prev)
            newState["billing"]["state"] = Object.keys(provinceOptions)[0]
            return newState
          })
        }
      }
    }
  }, [provinceOptions])

  // Handle Account info submission
  const submitAddress = (values) => {
    setLoading(true)
    const { token } = getUser()

    // Map values to WooCommerce structure
    let shippingFirstName = values.first_name
    let shippingLastName = values.last_name
    if (
      typeof values.shipping_name == "string" &&
      values.shipping_name.length
    ) {
      const shippingNameSplit = values.shipping_name
        .split(" ")
        .filter((word) => word.length)
      if (shippingNameSplit.length) {
        shippingFirstName = shippingNameSplit[0]
        if (shippingNameSplit.length == 1) {
          shippingLastName = " "
        } else {
          shippingLastName = shippingNameSplit.slice(1).join(" ")
        }
      }
    }

    let screenSize = 0
    if (
      typeof window == "object" &&
      typeof window.screen == "object" &&
      typeof window.screen.width != "undefined"
    ) {
      screenSize = window.screen.width
    }
    let device = "Desktop"
    if (screenSize < windowSizes.mobile) {
      device = "Mobile"
    } else if (screenSize <= windowSizes.tablet) {
      device = "Tablet"
    }

    const tmpAddress = {
      googleData,
      screenSize,
      device,
      first_name: values.first_name,
      last_name: values.last_name,
      birthday: values.bday,
      address_latitude: pinLatLng.lat,
      address_longitude: pinLatLng.lng,
      selected_branch: selectedBranch,
      billing: {
        first_name: shippingFirstName,
        last_name: shippingLastName,
        phone: values.phone,
        company: values.company,
        address_1: values.address_1,
        address_2: values.address_2,
        city: values.city,
        postcode: values.postcode,
        state: values.state,
        country: values.country,
      },
    }
    // We allow user to leave out address_2 but WooCommerce requires a value
    if (!tmpAddress.billing.address_2) {
      tmpAddress.billing.address_2 = "-"
    }

    /* Update customer address */
    updateCustomer(tmpAddress, token, performLogout)
      .then((res) => {
        if (res.billing) {
          setAccountData(res)
          setChangesSaved(true)
          // Update existing co-ords if map has been loaded
          if (googleMap && res.address_latitude && res.address_longitude) {
            safeSetPinLatLng({
              lat: res.address_latitude,
              lng: res.address_longitude,
            })
          }
          setApiError(false)
          setLoading(false)
          if (typeof continueCallback === "function") continueCallback(true)
        } else {
          // eslint-disable-next-line no-throw-literal
          throw "Bad response"
        }
      })
      .catch((error) => {
        console.log(error)
        setApiError(true)
        fetchAddress()
      })
  }

  // Get the selected Google "Place" and save it
  const googlePopulateAddress = () => {
    // Get "place" address data
    const place = googleAutocomplete.getPlace()
    console.log("Google Address Selected: ", place)

    // Update pin coordinates
    if (place.geometry && place.geometry.location) {
      console.log("Google Address setting pin")
      safeSetPinLatLng({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      })
    }
    savePlaceData(place)
  }

  // Save the Google "Place" data to the accountData structure
  const savePlaceData = (place) => {
    // Update values structure
    setGoogleData((prev) => {
      let newState = Object.assign({}, prev)

      // Populate company
      if (place.types.includes("establishment")) {
        const company_string = place.name
        newState["company"] = company_string
      } else {
        newState["company"] = ""
      }

      // Populate address_1
      let address_1_string = ""
      const street_number = place.address_components.find((it) =>
        it.types.includes("street_number")
      )
      const street_name = place.address_components.find((it) =>
        it.types.includes("route")
      )
      if (street_number && street_name) {
        address_1_string = `${street_number["long_name"]} ${street_name["long_name"]}`
      } else if (street_name) {
        address_1_string = street_name["long_name"]
      }
      newState["address_1"] = address_1_string

      // Populate address_2
      let address_2_string = ""
      const address_2 = place.address_components.find((it) =>
        it.types.includes("sublocality")
      )
      // If no address_1 was populated, use address_2 for address_1
      if (address_2) {
        address_2_string = address_2["long_name"]
        if (!address_1_string) {
          newState["address_1"] = address_2_string
          newState["address_2"] = ""
        } else {
          newState["address_2"] = address_2_string
        }
      }

      // Populate city
      let city_string = ""
      const city = place.address_components.find((it) =>
        it.types.includes("locality")
      )
      if (city) city_string = city["long_name"]
      newState["city"] = city_string

      // Populate postcode
      let postcode_string = ""
      const postcode = place.address_components.find((it) =>
        it.types.includes("postal_code")
      )
      if (postcode) postcode_string = postcode["short_name"]
      newState["postcode"] = postcode_string

      // Populate state
      let state_string = ""
      const state = place.address_components.find((it) =>
        it.types.includes("administrative_area_level_1")
      )
      if (state) state_string = state["short_name"]
      newState["state"] = state_string

      // Populate country
      let country_string = ""
      const country = place.address_components.find((it) =>
        it.types.includes("country")
      )
      if (country) country_string = country["short_name"]
      newState["country"] = country_string

      return newState
    })
  }

  // Use browser geolocation abilities
  const useBrowserLocation = () => {
    // Try HTML5 geolocation.
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          console.log("User Location found: ", newLatLng)
          saveLatLngToAddress(newLatLng)
        },
        () => {
          console.log("User denied location")
        }
      )
    }
  }

  // Render options dropdown with all countries
  const CountryOptions = () => {
    return (
      <>
        {Object.entries(countryOptions).map((country) => {
          return (
            <option key={country[0]} value={country[0]}>
              {country[1]}
            </option>
          )
        })}
      </>
    )
  }
  // Render options dropdown with all provinces
  const ProvinceOptions = () => {
    return (
      <>
        <option key={""} value={""}>
          {"Select the delivery Province"}
        </option>
        {Object.entries(provinceOptions).map((province) => {
          return (
            <option key={province[0]} value={province[0]}>
              {province[1]}
            </option>
          )
        })}
      </>
    )
  }

  const handlePostalCodeChange = (event) => {
    if (!pinLatLng) {
      if (event && event.target && event.target.value) {
        const newPostalCode = event.target.value
        if (newPostalCode.length == 4) {
          if (Number.isInteger(parseInt(newPostalCode))) {
            fetchLatLngFromPostcode(newPostalCode)
          }
        }
      }
    }
  }

  // If error, show error
  if (apiError) {
    return (
      <>
        <p>The following error occured while fetching your Account Info:</p>
        <p>{apiError}</p>
      </>
    )
  }

  return (
    <BlurLoader loading={loading} message="Updating">
      {!submitError ? null : (
        <SubmitErrorStyling>{submitError}</SubmitErrorStyling>
      )}
      <Formik
        initialValues={{
          first_name: accountData.first_name || "",
          last_name: accountData.last_name || "",
          bday: accountData.bday || "",
          shipping_name:
            (accountData.billing &&
            accountData.billing.first_name &&
            accountData.billing.last_name != " "
              ? `${accountData.billing.first_name} ${accountData.billing.last_name}`
              : accountData.billing && accountData.billing.first_name) || "",
          phone: (accountData.billing && accountData.billing.phone) || "",
          company: (accountData.billing && accountData.billing.company) || "",
          google_address_search: "",
          address_1:
            (accountData.billing && accountData.billing.address_1) || "",
          address_2:
            (accountData.billing && accountData.billing.address_2) || "",
          city: (accountData.billing && accountData.billing.city) || "",
          postcode: (accountData.billing && accountData.billing.postcode) || "",
          state: (accountData.billing && accountData.billing.state) || "",
          country: (accountData.billing && accountData.billing.country) || "ZA",
        }}
        enableReinitialize={true}
        validate={(values) => validate(values, onPage, fetchLatLngFromPostcode)}
        onSubmit={submitAddress}
      >
        {({
          errors,
          touched,
          values,
          validateForm,
          submitForm,
          setFieldValue,
        }) => {
          if (
            typeof values == "object" &&
            typeof values.google_address_search == "string" &&
            values.google_address_search != googleSearchText
          ) {
            setGoogleSearchText(values.google_address_search)
          }
          const [googleAutocompletePopulated, setGoogleAutocompletePopulated] =
            useState(false)
          // Refresh field values
          useEffect(() => {
            if (googleData && googleData.address_1) {
              setFieldValue("company", googleData.company)
              setFieldValue("address_1", googleData.address_1)
              setFieldValue("address_2", googleData.address_2)
              setFieldValue("city", googleData.city)
              setFieldValue("postcode", googleData.postcode)
              setFieldValue("state", googleData.state)
              setFieldValue("country", googleData.country)
              setSubmitError("")
              setGoogleAutocompletePopulated(true)
            }
          }, [googleData])

          useEffect(() => {
            if (googleAutocompletePopulated) {
              validateForm()
            }
          }, [googleAutocompletePopulated])

          // Try to submit account
          useEffect(() => {
            if (tryToSubmitAccount) {
              validateForm()
                .then((res) => {
                  if (!loading && !countriesLoading) {
                    submitForm()
                  }
                })
                .catch(() => {
                  if (
                    !googleAutocompletePopulated &&
                    !showManualAddressSection
                  ) {
                    setSubmitError(
                      "Please enter your address in the Address Search bar to populate your address"
                    )
                  }
                })
              setTryToSubmitAccount(false)
            }
          }, [tryToSubmitAccount])

          // Try to submit account
          useEffect(() => {
            if (loading || countriesLoading) {
              setReadyToSubmitAccount(false)
            } else {
              setReadyToSubmitAccount(true)
            }
          }, [loading, countriesLoading])

          return (
            <Form id="account-form">
              <Container className={condense && "condense"}>
                <Left className="left">
                  <Title>Personal Information</Title>
                  <Personal errors={errors} touched={touched} />
                </Left>
                <Right>
                  <Title>Delivery Address</Title>
                  <Delivery
                    pinLatLng={pinLatLng}
                    showManualAddressSection={showManualAddressSection}
                    errors={errors}
                    touched={touched}
                    handlePostalCodeChange={handlePostalCodeChange}
                  >
                    {Object.keys(provinceOptions).length > 0 && (
                      <ProvinceContainer light className="select">
                        <label htmlFor="state">Province:</label>
                        <Field
                          as="select"
                          disabled={countriesLoading}
                          id="state"
                          type="text"
                          name="state"
                          autoComplete="shipping address-level1"
                          style={getStyles(errors, touched, "state")}
                        >
                          <ProvinceOptions values={values} />
                        </Field>
                        <ErrorMessage
                          name="state"
                          render={(msg) => <div className="error">{msg}</div>}
                          className="error"
                        />
                      </ProvinceContainer>
                    )}
                  </Delivery>
                </Right>
              </Container>
              {onPage !== "checkout" && (
                <VertPad>
                  <ButtonRow>
                    <BtnSquare
                      onClick={() => {
                        if (
                          !googleAutocompletePopulated &&
                          !showManualAddressSection &&
                          !pinLatLng
                        ) {
                          setSubmitError(
                            "Please enter your address in the Address Search bar to populate your address"
                          )
                        }
                      }}
                      asButton
                      type="submit"
                      disabled={loading || countriesLoading}
                    >
                      SAVE CHANGES
                    </BtnSquare>
                  </ButtonRow>
                </VertPad>
              )}
              <GoogleMapStyles hidden={!pinLatLng}>
                <p>
                  Please ensure the map pin corresponds to your chosen address:
                </p>
                <div id="map" />
              </GoogleMapStyles>
            </Form>
          )
        }}
      </Formik>
      {!showManualAddressButton || pinLatLng ? null : (
        <ManualAddressStyling>
          <div>
            Couldn&apos;t find your address with the Google Address Search bar
            above? Click the button below to enter your address manually.
          </div>
          <button
            onClick={() => {
              setShowManualAddressButton(false)
              setShowManualAddressSection(true)
            }}
          >
            Enter address manually
          </button>
        </ManualAddressStyling>
      )}
      <StyledH4 onClick={performLogout}>
        Not your account? Click here to log out.
      </StyledH4>
    </BlurLoader>
  )
}

// ======================
// 	🔧🔧 HELPERS 🔧🔧
// ======================
function getStyles(errors, touched, fieldName) {
  if (getIn(errors, fieldName) && getIn(touched, fieldName)) {
    return {
      border: "1px solid red",
    }
  }
}

const validate = (values, onPage, fetchLatLngFromPostcode) => {
  const errors = {}

  // on checkout, the personal details aren't shown so don't check them
  if (onPage !== "checkout" && !values.first_name) {
    errors.first_name = "Your First Name is required"
  }
  if (onPage !== "checkout" && !values.last_name) {
    errors.last_name = "Your Last Name is required"
  }

  if (!values.shipping_name) {
    errors.shipping_name = "A recipient Name and Surname is required"
  }
  if (
    values.bday &&
    !/^([1-9]|0[1-9]|[12][0-9]|3[01])[/]([1-9]|0[1-9]|1[012])[0/](19|20)\d\d$/.test(
      values.bday
    )
  ) {
    errors.bday = "Please enter a valid date in the format DD/MM/YYYY"
  }
  if (!values.shipping_name) {
    errors.name = "Delivery Contact Name is required"
  }
  if (!values.address_1) {
    errors.address_1 = "Street Address is required"
  }
  if (!values.city) {
    errors.city = "City is required"
  }
  if (!values.state) {
    errors.state = "Province is required"
  }
  if (!values.country) {
    errors.country = "Country is required"
  }
  if (!values.phone) {
    errors.phone = "Phone number is required"
  } else if (!/^^\d\d\d\s*\d\d\d\s*\d\s*\d\d\d$$/.test(values.phone)) {
    errors.phone = "Please use a 10 digit phone number"
  }
  if (!values.postcode) {
    errors.postcode = "Postal code is required"
  } else if (!/^[0-9]{4}$/.test(values.postcode)) {
    errors.postcode = "Please use a 4 digit postal code"
  } else {
    fetchLatLngFromPostcode(values.postcode)
  }
  return errors
}

// ======================
// 	💅🏼💅🏼 STYLES 💅🏼💅🏼
// ======================
const StyledH4 = styled.h4`
  margin-top: 40px;
  cursor: pointer;
  text-align: center;
`
const ManualAddressStyling = styled.div`
  text-align: center;
  button {
    margin-top: 20px;
    width: 100%;
    color: white;
    padding: 20px;
    border-radius: 4px;
    text-transform: uppercase;
    border-width: 0px;
    background: ${({ theme }) => theme.colors.ocean["080"]};
    &:disabled {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.tusk["080"]};
      &:hover {
        cursor: not-allowed;
        background: ${({ theme }) => theme.colors.tusk["080"]};
      }
    }
    &:hover {
      cursor: pointer;
      background: ${({ theme }) => theme.colors.ocean["100"]};
    }
  }
`
const SubmitErrorStyling = styled.div`
  color: red;
`
const ProvinceContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0px 0px 15px 0px;

  select {
    padding: 15px;
    border-radius: 4px;
    background: #f6f6f6;
    border: 1px solid #cfd5db;
  }

  div.error {
    margin: 10px 0;
    color: black;
  }

  span.icon {
    position: absolute;
    top: 57px;
    right: 5px;
    cursor: pointer;
  }

  label {
    display: flex;
    color: ${({ light }) => (light ? "#223247" : "#f0f1f3")};
    font-family: Roboto;
    font-size: 16px;
    padding: 10px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    div {
      color: red;
    }
    span {
      color: #fd3237;
      text-align: right;
    }
  }

  input,
  select {
    border: 1px solid ${({ light }) => (light ? "#CFD5DB" : "#3e4b5c")};
    // input font size must be at least 16px otherwise it causes zoom-in on mobile
    font-size: 16px;
    border-radius: 3px;
    background-color: ${({ light }) => (light ? "#FFFFFF" : "#223247")};
    padding: 15px;
    color: ${({ light }) => (light ? "color: #223247" : "white")};
  }
`

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;

  grid-gap: 40px;

  &.condense {
    display: block;
    .left {
      display: none;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakLarge}) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: ${({ theme }) => theme.breakMedium}) {
    grid-template-columns: 1fr;
  }
`
const GoogleMapStyles = styled.div`
  @media (max-width: ${({ theme }) => theme.breakLarge}) {
    display: none;
  }
  p {
    text-align: center;
    padding: 10px;
    font-style: italic;
    font-size: 16px;
  }
  div {
    height: 500px;
  }
  ${(props) => (props.hidden ? "display: hidden" : null)}
`
const VertPad = styled.div`
  padding: 20px 0;
`
const Left = styled.div``
const Title = styled.h3`
  font-weight: 500;
`
const Right = styled.div``

export default Info
